import { stripHTMLTags } from '../easyfy.utils';

export type Politeness = 'polite' | 'assertive';

const REGION_ID: Record<Politeness, string> = {
  polite: 'sr-toasts',
  assertive: 'sr-alerts'
} as const;

const lastMsg: Record<Politeness, string> = { polite: '', assertive: '' };
const lastAt: Record<Politeness, number> = { polite: 0, assertive: 0 };

export interface AnnounceTimingOptions {
  /** Minimum TTL in milliseconds (default: 5000) */
  minReadingTimeMs?: number;

  /** Maximum TTL in milliseconds (default: 20000) */
  maxReadingTimeMs?: number;

  /** Base delay added to all calculations in milliseconds (default: 4500) */
  baseDelayTimeMs?: number;

  /** Characters per second reading rate (default: 80) */
  charsPerSecond?: number;
}

/**
 * Configuration options for announcing content to users.
 *
 * @interface AnnounceOptions
 */
export interface AnnounceOptions {
  /** Time-to-live in milliseconds for the announcement before it expires */
  ttlMs?: number;

  /** Deduplication window in milliseconds to prevent duplicate announcements */
  dedupeMs?: number;

  /** Language code for the announcement (e.g., 'en', 'es', 'fr') */
  lang?: string;

  /** Whether to sanitize the content before announcing */
  sanitize?: boolean;

  /** Maximum number of DOM nodes to process for the announcement */
  maxNodes?: number;

  /** Timing configuration for TTL calculation when ttlMs is not provided */
  timing?: AnnounceTimingOptions;
}

/**
 * Calculates a time-to-live (TTL) value in milliseconds based on text length.
 *
 * The TTL is computed as a base delay plus estimated reading time, constrained
 * within minimum and maximum bounds. Reading time assumes approximately 80
 * characters per second.
 *
 * @param text - The text string to calculate TTL for
 * @returns TTL in milliseconds, ranging from 5000 ms to 20000 ms
 *
 * @example
 * ttlFor('Hello') // Returns 6500 ms (4500 base + 2000 reading = 6500, above the 5000 min)
 * ttlFor('A'.repeat(800)) // Returns 20000 ms (4500 base + 20000 reading = 24500, clamped to 20000 max)
 */
function ttlFor(
  text: string,
  {
    minReadingTimeMs = 5000,
    maxReadingTimeMs = 20000,
    baseDelayTimeMs = 4500,
    charsPerSecond = 40
  }: AnnounceTimingOptions = {}
): number {
  const readingTimeMs = Math.ceil((text?.length ?? 0) / charsPerSecond) * 1000;
  return Math.max(
    minReadingTimeMs,
    Math.min(maxReadingTimeMs, baseDelayTimeMs + readingTimeMs)
  );
}

/**
 * Announces a message to screen readers using ARIA live regions.
 *
 * Creates a temporary DOM element in the appropriate live region that will be
 * read aloud by assistive technologies. Messages are deduplicated within a
 * time window and automatically cleaned up after their TTL expires.
 *
 * @param message - The text message to announce
 * @param politeness - The ARIA politeness level ('polite' or 'assertive')
 * @param options - Configuration options for the announcement
 * @param options.ttlMs - Time-to-live in milliseconds (defaults to calculated value based on text length)
 * @param options.dedupeMs - Deduplication window in milliseconds to prevent duplicate announcements (default: 4000)
 * @param options.lang - Language code for the announcement (e.g., 'en', 'es', 'fr')
 * @param options.sanitize - Whether to strip HTML tags and normalize whitespace (default: true)
 * @param options.maxNodes - Maximum number of announcement nodes to keep in the live region (default: 5)
 * @param options.timing - Timing configuration for TTL calculation when ttlMs is not provided
 * @param options.timing.minReadingTimeMs - Minimum TTL in milliseconds (default: 5000)
 * @param options.timing.maxReadingTimeMs - Maximum TTL in milliseconds (default: 20000)
 * @param options.timing.baseDelayTimeMs - Base delay added to all calculations in milliseconds (default: 4500)
 * @param options.timing.charsPerSecond - Characters per second reading rate (default: 40)
 *
 * @returns The created DOM element, or null if the announcement was skipped
 *
 * @example
 * // Basic announcement
 * announce('Form saved successfully');
 *
 * // Urgent announcement with custom options
 * announce('Error occurred', 'assertive', {
 *   ttlMs: 8000,
 *   sanitize: false
 * });
 *
 * // Custom timing configuration
 * announce('Long message', 'polite', {
 *   timing: {
 *     minReadingTimeMs: 3000,
 *     charsPerSecond: 60
 *   }
 * });
 */
export function announce(
  message: string,
  politeness: Politeness = 'polite',
  options: AnnounceOptions = {}
): HTMLDivElement | null {
  // Destructure options with fallback defaults for missing properties.
  const {
    ttlMs,
    dedupeMs = 4000,
    lang,
    sanitize = true,
    maxNodes = 5,
    timing = {}
  } = options;

  try {
    // Convert the message to string and sanitize if requested.
    const rawMessage = typeof message === 'string' ? message : String(message);
    const spokenMessage = sanitize
      ? stripHTMLTags(rawMessage)
          .replace(/\u00A0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      : rawMessage;

    // Skip empty messages after sanitization.
    if (!spokenMessage) {
      return null;
    }

    // One timestamp for everything.
    const stamp = new Date();
    const epoch = stamp.getTime();

    // De-dupe per region.
    if (
      spokenMessage === lastMsg[politeness] &&
      epoch - lastAt[politeness] < dedupeMs
    ) {
      return null;
    }

    // Update deduplication tracking.
    lastMsg[politeness] = spokenMessage;
    lastAt[politeness] = epoch;

    // Find the appropriate live region.
    const region = document.getElementById(REGION_ID[politeness]);
    if (!region) {
      return null;
    }

    // Create announcement element.
    const item = document.createElement('div');
    if (lang) {
      item.setAttribute('lang', lang);
    }
    item.dataset.announcedAt = stamp.toISOString();
    item.textContent = spokenMessage;
    region.appendChild(item);

    // Keep only the newest N nodes.
    while (region.childElementCount > maxNodes) {
      region.firstElementChild?.remove();
    }

    // Remove after TTL (computed unless overridden).
    const ttl = ttlMs ?? ttlFor(spokenMessage, options.timing);
    window.setTimeout(() => item.remove(), ttl);

    return item;
  } catch (error) {
    console.error('Failed to announce message:', error);
    return null;
  }
}
